import { DatePreset, DATE_PRESETS, DateRange } from '../types/filters';
import { Timestamp } from 'firebase/firestore';

// General purpose date formatter for Firestore Timestamps and Date objects
// Returns MM/DD/YYYY format or fallback for invalid dates
export function formatDate(dateInput: Date | Timestamp | string | null | undefined): string {
  if (!dateInput) return '—';
  
  try {
    let date: Date;
    
    // Handle Firestore Timestamp
    if (dateInput && typeof dateInput === 'object' && 'toDate' in dateInput) {
      date = (dateInput as Timestamp).toDate();
    }
    // Handle Date object
    else if (dateInput instanceof Date) {
      date = dateInput;
    }
    // Handle string
    else if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    }
    else {
      return '—';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '—';
    
    // Format as MM/DD/YYYY
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '—';
  }
}

// Format date as MM/DD/YYYY with Eastern Time
export function formatDateET(date: Date | string | null): string {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '-';
  
  const options: Intl.DateTimeFormatOptions = { 
    month: '2-digit', 
    day: '2-digit', 
    year: 'numeric',
    timeZone: 'America/New_York'
  };
  
  return new Intl.DateTimeFormat('en-US', options).format(dateObj);
}

// Format date as YYYY-MM-DD
export function formatDateForInput(date: Date): string {
  // Create a new date object with the date's year, month, and day
  // This ensures we're working with the local date, not UTC
  const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Format date as MM/DD/YYYY for display
export function formatDateForDisplay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

// Get date range for a preset
export function getDateRangeForPreset(preset: DatePreset): DateRange {
  // Use the current date and time in the local timezone
  const now = new Date();
  // Create a date object for today at midnight in the local timezone
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  
  
  switch(preset) {
    case DATE_PRESETS.ALL_DATES: {
      const range = {
        from: new Date(2025, 2, 1), // March 1, 2025
        to: new Date(), // Current date
        label: 'All Dates'
      };
      return range;
    }
    case DATE_PRESETS.TODAY: {
      const range = {
        from: today,
        to: today,
        label: 'Today' // Simple label as requested
      };
      return range;
    }
    
    case DATE_PRESETS.YESTERDAY: {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const range = {
        from: yesterday,
        to: yesterday,
        label: 'Yesterday' // Simple label as requested
      };
      return range;
    }
    
    case DATE_PRESETS.THIS_WEEK: {
      // Get Monday of current week
      const monday = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Sunday
      monday.setDate(today.getDate() - diff);
      
      // Format dates without slashes to avoid indentation issues
      const mondayStr = `${monday.getMonth() + 1}/${monday.getDate()}/${monday.getFullYear().toString().substr(2)}`;
      const todayStr = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear().toString().substr(2)}`;
      
      const range = {
        from: monday,
        to: today,
        // Using a different format for the date range to fix indentation
        label: `This Week (${mondayStr} to ${todayStr})`
      };
      return range;
    }
    
    case DATE_PRESETS.LAST_WEEK: {
      // Get Monday of last week
      const mondayLastWeek = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Sunday
      mondayLastWeek.setDate(today.getDate() - diff - 7);
      
      // Get Sunday of last week
      const sundayLastWeek = new Date(mondayLastWeek);
      sundayLastWeek.setDate(mondayLastWeek.getDate() + 6);
      
      // Format dates without slashes to avoid indentation issues
      const mondayStr = `${mondayLastWeek.getMonth() + 1}/${mondayLastWeek.getDate()}/${mondayLastWeek.getFullYear().toString().substr(2)}`;
      const sundayStr = `${sundayLastWeek.getMonth() + 1}/${sundayLastWeek.getDate()}/${sundayLastWeek.getFullYear().toString().substr(2)}`;
      
      return {
        from: mondayLastWeek,
        to: sundayLastWeek,
        // Using a different format for the date range to fix indentation
        label: `Last Week (${mondayStr} to ${sundayStr})`
      };
    }
    
    case DATE_PRESETS.THIS_MONTH: {
      // First day of current month
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      
      return {
        from: firstDayOfMonth,
        to: today,
        label: 'This Month' // Simple label as requested
      };
    }
    
    case DATE_PRESETS.LAST_MONTH: {
      // First day of last month
      const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      
      // Last day of last month
      const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      
      return {
        from: firstDayOfLastMonth,
        to: lastDayOfLastMonth,
        label: 'Last Month' // Simple label as requested
      };
    }
    
    case DATE_PRESETS.THIS_YEAR: {
      // First day of current year
      const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
      
      return {
        from: firstDayOfYear,
        to: today,
        label: 'This Year' // Simple label as requested
      };
    }
    
    case DATE_PRESETS.LAST_YEAR: {
      // First day of last year
      const firstDayOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
      
      // Last day of last year
      const lastDayOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
      
      return {
        from: firstDayOfLastYear,
        to: lastDayOfLastYear,
        label: 'Last Year' // Simple label as requested
      };
    }
    
    case DATE_PRESETS.DATE_RANGE:
    default:
      return {
        from: today,
        to: today,
        label: 'Date Range'
      };
  }
}

// Get preset options with labels
export function getDatePresetOptions() {
  return [
    { value: DATE_PRESETS.ALL_DATES, label: 'All Dates' },
    { value: DATE_PRESETS.TODAY, label: getDateRangeForPreset(DATE_PRESETS.TODAY).label },
    { value: DATE_PRESETS.YESTERDAY, label: getDateRangeForPreset(DATE_PRESETS.YESTERDAY).label },
    { value: DATE_PRESETS.THIS_WEEK, label: getDateRangeForPreset(DATE_PRESETS.THIS_WEEK).label },
    { value: DATE_PRESETS.LAST_WEEK, label: getDateRangeForPreset(DATE_PRESETS.LAST_WEEK).label },
    { value: DATE_PRESETS.THIS_MONTH, label: getDateRangeForPreset(DATE_PRESETS.THIS_MONTH).label },
    { value: DATE_PRESETS.LAST_MONTH, label: getDateRangeForPreset(DATE_PRESETS.LAST_MONTH).label },
    { value: DATE_PRESETS.THIS_YEAR, label: getDateRangeForPreset(DATE_PRESETS.THIS_YEAR).label },
    { value: DATE_PRESETS.LAST_YEAR, label: getDateRangeForPreset(DATE_PRESETS.LAST_YEAR).label }
  ];
}
